import { memo, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';

import { Button, Modal } from '@components/common';

import { IConsistencyCheckActions } from '@interfaces/cmModule/consistencyCheck';
import { TConsistencyCheckRanGetAvailableTemplatesDTO } from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { IActionMenu } from '@interfaces/components/table';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { ASSIGN_REFERENCE_TEMPLATE } from '../../constants';

import BulkAssignConsistencyCheckTemplate from './BulkAssignConsistencyCheckTemplate';
import { getAssignConsistencyCheckOptions, getAssignTemplateDTO } from './utils';

import styles from './AssignConsistencyCheckTemplateMenu.module.scss';

interface IAssignConsistencyCheckTemplateMenu extends IConsistencyCheckActions {
	isVisible: boolean;
	toggleVisibility: () => void;
}

const AssignConsistencyCheckTemplateMenu = ({
	currentPageIds,
	selectedIds,
	filter,
	isVisible,
	toggleVisibility,
}: IAssignConsistencyCheckTemplateMenu) => {
	const [isOpenBulkAssign, setIsOpenBulkAssign] = useState(false);
	const [groupParams, setGroupParams] = useState<TConsistencyCheckRanGetAvailableTemplatesDTO>();

	const openBulkAssign = useCallback(() => setIsOpenBulkAssign(true), [setIsOpenBulkAssign]);
	const closeBulkAssign = useCallback(() => setIsOpenBulkAssign(false), [setIsOpenBulkAssign]);

	const isNonEmptyFilter = useMemo(() => Object.values(filter).some(Boolean), [filter]);

	const options = useMemo(() => {
		return getAssignConsistencyCheckOptions(
			selectedIds.length > 0,
			isNonEmptyFilter,
		);
	}, [selectedIds, isNonEmptyFilter]);

	const onMenuItemClick = useCallback((action: IActionMenu<string>) => {
		if (action.disabled) {
			return;
		}

		const params = getAssignTemplateDTO({
			actionId: action.id,
			currentPageIds,
			selectedIds,
			filter,
		});

		setGroupParams(params);
		openBulkAssign();
	}, [
		openBulkAssign,
		currentPageIds,
		selectedIds,
		filter,
	]);

	return (
		<div className={styles.container}>
			<Modal
				isOpen={isOpenBulkAssign}
				onClose={closeBulkAssign}
				title={ASSIGN_REFERENCE_TEMPLATE}
				width={MODAL_WIDTH_580}
			>
				<BulkAssignConsistencyCheckTemplate
					closeForm={closeBulkAssign}
					groupParams={groupParams}
				/>
			</Modal>
			<Button
				onClick={toggleVisibility}
				secondary
				text='Assign template'
			/>
			{isVisible && (
				<div className={styles.menuWrapper}>
					{isVisible && options.map((action) => {
						return (
							<div
								key={action.text}
								className={cn(styles.menuItem, {
									[styles.disabled]: action.disabled,
								})}
								data-testid='action-item'
								onClick={() => onMenuItemClick(action)}
							>
								{action.text}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default memo(AssignConsistencyCheckTemplateMenu);
